import { createServerFn } from '@tanstack/react-start';
import { addMonths, isPast } from 'date-fns';
import z from 'zod';

import { logger } from '~ai-tutor/lib/logger/logger';
import { getAiTutorSettingsForToken } from '~ai-tutor/lib/service/aiTutorSettings';

import { resolveIdentity } from '../../auth/resolveIdentity';
import { userEventAccessRepository } from '../../ddb/userEventAccess';

const redeemTokenInputSchema = z.object({
  token: z.string().min(1, 'Token is required'),
});
type RedeemTokenInput = z.infer<typeof redeemTokenInputSchema>;

type RedeemTokenResult =
  | { success: true; eventId: string; accessValidUntil: Date }
  | { success: false; error: string };

const AI_TUTOR_ACCESS_TOKEN_VALIDITY_IN_MONTHS = 3;

const redeemToken = async ({ token }: RedeemTokenInput): Promise<RedeemTokenResult> => {
  const identity = await resolveIdentity();

  if (!identity || identity.type !== 'oidc') {
    logger.info('Redeem token attempt failed: OIDC authentication required');
    return { success: false, error: 'OIDC authentication required to redeem a token' };
  }

  const { userId } = identity;

  const aiTutorSettings = await getAiTutorSettingsForToken(token);
  const accessToken = aiTutorSettings?.accessTokens.find((t) => t.token === token);
  if (!aiTutorSettings || !accessToken?.enabled) {
    logger.info('Redeem token attempt failed: Invalid or disabled token');
    return { success: false, error: 'Invalid or disabled token' };
  }

  const eventId = aiTutorSettings.parent.id;
  const accessValidUntil = addMonths(
    aiTutorSettings.parent.endDate,
    AI_TUTOR_ACCESS_TOKEN_VALIDITY_IN_MONTHS,
  );

  const userEventRepo = userEventAccessRepository();
  const existingUserEventAccess = await userEventRepo.getUserEventAccess(userId, eventId);

  if (!existingUserEventAccess) {
    if (isPast(accessValidUntil)) {
      logger.info('Redeem token attempt failed: event is too old');
      return { success: false, error: 'event is too old' };
    }

    await userEventRepo.grantAccess({ userId, eventId, accessValidUntil, token });
    logger.info('Redeem token attempt succeeded: Access granted');
  }

  return {
    success: true,
    eventId,
    accessValidUntil,
  };
};

export const redeemTokenFn = createServerFn({ method: 'POST' })
  .validator(redeemTokenInputSchema)
  .handler(({ data }) => redeemToken(data));
